import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2 } from "lucide-react";
import { useConnectedApps } from "@/hooks/queries/use-apps";
import { useInitiateAppConnection } from "@/hooks/mutations/use-apps";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type AvailableApp = NonNullable<
  ReturnType<typeof useConnectedApps>["data"]
>[number];

type CategoryKey = "crm" | "calendarEmail" | "team" | "other";

/**
 * Connections grouped by what they unlock, Midday-style: every category states
 * *why* we ask in one line before offering the providers. Copy lives under
 * `integrations.categories.*`.
 *
 * The apps themselves come from the server (Composio's enabled auth configs),
 * so this only decides where the ones we know about are placed — anything
 * enabled in Composio that is not listed here still shows, under "other".
 */
const CATEGORY_ORDER: { key: CategoryKey; apps: string[] }[] = [
  { key: "crm", apps: ["HUBSPOT", "SALESFORCE", "PIPEDRIVE"] },
  { key: "calendarEmail", apps: ["GOOGLECALENDAR", "GMAIL", "OUTLOOK"] },
  { key: "team", apps: ["SLACK"] },
];

interface IntegrationsStepProps {
  businessId: string | null;
  errorMsg: string | null;
  /** Where the OAuth flow returns; defaults to the signup onboarding step. */
  oauthRedirectPath?: string;
}

export default function IntegrationsStep({
  businessId,
  errorMsg,
  oauthRedirectPath = "/onboarding?s=integrations",
}: IntegrationsStepProps) {
  const { t } = useTranslation("onboarding");
  const { data: apps } = useConnectedApps(businessId ?? undefined);
  const initiate = useInitiateAppConnection();
  const [pendingApp, setPendingApp] = useState<string | null>(null);
  const [connectError, setConnectError] = useState(false);

  const sections = useMemo(() => {
    const available = apps ?? [];
    const byId = new Map(available.map((app) => [app.id, app]));
    const placed = new Set<string>();

    const categorised = CATEGORY_ORDER.map(({ key, apps: ids }) => {
      const items = ids
        .map((id) => byId.get(id))
        .filter((app): app is AvailableApp => app != null);
      items.forEach((app) => placed.add(app.id));
      return { key, items };
    });

    // Everything enabled in Composio that no category claims. Keeps a newly
    // enabled provider reachable from onboarding without a deploy.
    const rest = available.filter((app) => !placed.has(app.id));
    return [...categorised, { key: "other" as CategoryKey, items: rest }].filter(
      (section) => section.items.length > 0,
    );
  }, [apps]);

  async function handleConnect(appId: string) {
    if (!businessId) return;
    setConnectError(false);
    setPendingApp(appId);
    try {
      const redirectUrl = `${window.location.origin}${oauthRedirectPath}`;
      const result = await initiate.mutateAsync({
        businessId,
        appName: appId,
        redirectUrl,
      });
      window.location.href = result.redirectUrl;
    } catch {
      setConnectError(true);
      setPendingApp(null);
    }
  }

  return (
    <div className="space-y-7">
      {errorMsg && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm font-semibold text-destructive"
        >
          {errorMsg}
        </div>
      )}

      {sections.map(({ key, items }) => (
        <section key={key} className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              {t(`integrations.categories.${key}.title`)}
              <span className="font-normal text-muted-foreground">
                {" — "}
                {t(`integrations.categories.${key}.motivation`)}
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {items.map((app) => {
              const connected = app.connected;
              const comingSoon = app.badge === "coming-soon";
              const pending = pendingApp === app.id;
              const logoSrc =
                app.logoUrl ??
                (app.faviconDomain
                  ? `https://www.google.com/s2/favicons?domain=${app.faviconDomain}&sz=64`
                  : null);
              return (
                <Button
                  key={app.id}
                  type="button"
                  variant="outline"
                  className={cn(
                    "h-10 justify-start gap-2 font-normal",
                    connected && "border-emerald-300/60 bg-emerald-500/[0.06]",
                  )}
                  disabled={
                    connected || comingSoon || pending || !businessId
                  }
                  onClick={() => handleConnect(app.id)}
                >
                  {logoSrc ? (
                    <img
                      src={logoSrc}
                      alt=""
                      className={cn("h-4 w-4", comingSoon && "opacity-40")}
                    />
                  ) : (
                    <span className="flex h-4 w-4 items-center justify-center rounded-sm bg-muted text-[9px] font-semibold text-muted-foreground">
                      {app.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className="truncate text-sm">
                    {connected || comingSoon
                      ? app.name
                      : `${t("integrations.connect")} ${app.name}`}
                  </span>
                  {connected && (
                    <Check
                      className="ml-auto h-4 w-4 shrink-0 text-emerald-600"
                      strokeWidth={2.5}
                    />
                  )}
                  {comingSoon && (
                    <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {t("integrations.comingSoon")}
                    </span>
                  )}
                  {pending && (
                    <Loader2 className="ml-auto h-4 w-4 shrink-0 animate-spin" />
                  )}
                </Button>
              );
            })}
          </div>
        </section>
      ))}

      {connectError && (
        <p role="alert" className="text-sm text-destructive">
          {t("integrations.connectError")}
        </p>
      )}

      <p className="text-center text-xs text-muted-foreground">
        {t("integrations.reassurance")}
      </p>
    </div>
  );
}
